import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  ShoppingCart,
  Package,
  Plus,
  Building2,
  Search,
  LayoutGrid,
  LayoutList,
  Star,
  Award,
  Clock,
  AlertCircle,
  TrendingUp,
  Calendar,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react';
import {
  useSuppliers,
  usePurchaseRequests,
  usePurchaseOrders,
  useGoodsReceivedNotes,
  type Supplier,
  type PurchaseRequest,
  type PurchaseOrder,
  type GoodsReceivedNote
} from '../../api/queries/purchasing.queries';
import {
  useSendPurchaseOrder,
  useDeleteSupplier,
} from '../../api/mutations/purchasing.mutations';
import { apiClient } from '../../api/client';
import { useAuthStore } from '../../stores/auth.store';
import { useBranchStore } from '../../stores/branch.store';

// Shared components
import { MetricCard, EmptyState } from './components/shared';

// Card components
import { SupplierCard, PRCard, POCard, GRNCard } from './components/cards';

// Modal components
import SupplierModal from './components/SupplierModal';
import SupplierDetailModal from './components/SupplierDetailModal';
import CreatePRModal from './components/CreatePRModal';
import PRDetailModal from './components/PRDetailModal';
import CreatePOModal from './components/CreatePOModal';
import PODetailModal from './components/PODetailModal';
import CreateGRNModal from './components/CreateGRNModal';
import GRNDetailModal from './components/GRNDetailModal';

type TabType = 'suppliers' | 'pr' | 'po' | 'grn';
type ViewMode = 'cards' | 'table';

function PurchasingPage() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const selectedBranchId = useBranchStore((state) => state.selectedBranchId);
  const [activeTab, setActiveTab] = useState<TabType>('pr');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('cards');

  // Modal states
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showSupplierDetail, setShowSupplierDetail] = useState<Supplier | null>(null);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [showPRModal, setShowPRModal] = useState(false);
  const [selectedPR, setSelectedPR] = useState<PurchaseRequest | null>(null);
  const [showPOModal, setShowPOModal] = useState(false);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [showGRNModal, setShowGRNModal] = useState(false);
  const [selectedGRN, setSelectedGRN] = useState<GoodsReceivedNote | null>(null);
  const [prForPO, setPrForPO] = useState<PurchaseRequest | null>(null);
  const [poForGRN, setPoForGRN] = useState<PurchaseOrder | null>(null);

  // Queries
  const { data: suppliers, refetch: refetchSuppliers } = useSuppliers({
    search: searchQuery || undefined,
    isActive: true
  });
  const { data: purchaseRequestsData, refetch: refetchPRs } = usePurchaseRequests({
    branchId: selectedBranchId || undefined,
    status: statusFilter || undefined
  });
  const { data: purchaseOrdersData, refetch: refetchPOs } = usePurchaseOrders({
    status: statusFilter || undefined
  });
  const { data: grnsData, refetch: refetchGRNs } = useGoodsReceivedNotes({
    status: statusFilter || undefined
  });

  const purchaseRequests = purchaseRequestsData?.requests || [];
  const purchaseOrders = purchaseOrdersData?.orders || [];
  const grns = grnsData?.grns || [];

  // Mutations
  const sendPO = useSendPurchaseOrder();
  const deleteSupplier = useDeleteSupplier();

  // Computed metrics for dashboard
  const supplierMetrics = useMemo(() => {
    if (!suppliers) return { total: 0, preferred: 0, avgQuality: 0, avgLeadTime: 0 };
    const preferred = suppliers.filter((s) => s.isPreferred).length;
    const withMetrics = suppliers.filter((s) => s.performanceMetrics?.qualityRating);
    const avgQuality = withMetrics.length > 0
      ? withMetrics.reduce((sum, s) => sum + (s.performanceMetrics?.qualityRating || 0), 0) / withMetrics.length
      : 0;
    const avgLeadTime = suppliers.length > 0
      ? suppliers.reduce((sum, s) => sum + (s.leadTimeDays || 0), 0) / suppliers.length
      : 0;
    return { total: suppliers.length, preferred, avgQuality, avgLeadTime: Math.round(avgLeadTime) };
  }, [suppliers]);

  const prMetrics = useMemo(() => {
    const pending = purchaseRequests.filter((pr) => pr.status === 'pending' || pr.status === 'pending_extra_approval').length;
    const urgent = purchaseRequests.filter((pr) => pr.urgency === 'urgent' || pr.urgency === 'high').length;
    const thisWeek = purchaseRequests.filter((pr) => {
      const date = new Date(pr.createdAt);
      const now = new Date();
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return date >= weekAgo;
    }).length;
    const totalEstimated = purchaseRequests.reduce((sum, pr) => sum + (pr.totalEstimatedCost || 0), 0);
    return { pending, urgent, thisWeek, totalEstimated };
  }, [purchaseRequests]);

  const poMetrics = useMemo(() => {
    const inTransit = purchaseOrders.filter((po) => po.status === 'sent' || po.status === 'confirmed').length;
    const overdue = purchaseOrders.filter((po) => {
      if (!po.expectedDeliveryDate || po.status === 'completed' || po.status === 'cancelled') return false;
      return new Date(po.expectedDeliveryDate) < new Date();
    }).length;
    const thisMonth = purchaseOrders.filter((po) => {
      const date = new Date(po.createdAt);
      const now = new Date();
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }).length;
    const total = purchaseOrders.reduce((sum, po) => sum + (po.total || 0), 0);
    return { inTransit, overdue, thisMonth, total };
  }, [purchaseOrders]);

  const grnMetrics = useMemo(() => {
    const pending = grns.filter((grn) => grn.status === 'pending_review' || grn.status === 'draft').length;
    const withIssues = grns.filter((grn) => grn.hasDiscrepancies).length;
    const completed = grns.filter((grn) => grn.status === 'completed').length;
    const quarantine = grns.filter((grn) =>
      grn.items.some((item: any) => item.discrepancyAction === 'quarantine')
    ).length;
    return { pending, withIssues, completed, quarantine };
  }, [grns]);

  const tabs = [
    { id: 'suppliers' as const, label: t('purchasing.suppliers'), icon: Building2 },
    { id: 'pr' as const, label: t('purchasing.purchaseRequests'), icon: FileText },
    { id: 'po' as const, label: t('purchasing.purchaseOrders'), icon: ShoppingCart },
    { id: 'grn' as const, label: t('purchasing.goodsReceived'), icon: Package },
  ];

  const handleApprovePR = async (pr: PurchaseRequest) => {
    if (!confirm(t('purchasing.confirmApprove'))) return;
    try {
      await apiClient.patch(`/purchasing/purchase-requests/${pr._id}/approve`);
      refetchPRs();
    } catch (error) {
      console.error('Failed to approve PR:', error);
    }
  };

  const handleRejectPR = async (pr: PurchaseRequest) => {
    const reason = prompt(t('purchasing.rejectionReason') || 'Reason:');
    if (!reason) return;
    try {
      await apiClient.patch(`/purchasing/purchase-requests/${pr._id}/reject`, { reason });
      refetchPRs();
    } catch (error) {
      console.error('Failed to reject PR:', error);
    }
  };

  const handleSendPO = async (po: PurchaseOrder) => {
    if (!confirm(t('purchasing.confirmSend'))) return;
    try {
      await sendPO.mutateAsync(po._id, {
        onSuccess: () => refetchPOs()
      });
    } catch (error) {
      console.error('Failed to send PO:', error);
    }
  };

  const handleDeleteSupplier = async (supplier: Supplier) => {
    if (!confirm(t('purchasing.confirmDeleteSupplier', { name: supplier.name }))) return;
    try {
      await deleteSupplier.mutateAsync(supplier._id);
      refetchSuppliers();
    } catch (error) {
      console.error('Failed to delete supplier:', error);
    }
  };

  const handleConvertToPO = (pr: PurchaseRequest) => {
    setPrForPO(pr);
    setShowPOModal(true);
  };

  const handleRecordGRN = (po: PurchaseOrder) => {
    setPoForGRN(po);
    setShowGRNModal(true);
  };

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `฿${(amount / 1000000).toFixed(1)}M`;
    }
    if (amount >= 1000) {
      return `฿${(amount / 1000).toFixed(1)}K`;
    }
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const canApprovePR = user?.role === 'HQ_PURCHASING' || user?.role === 'HQ_ADMIN';
  const canCreatePO = user?.role === 'HQ_PURCHASING' || user?.role === 'HQ_ADMIN';
  const canManageSuppliers = user?.role === 'HQ_PURCHASING' || user?.role === 'HQ_ADMIN';

  // Empty state configurations
  const emptyStates = {
    suppliers: {
      icon: Building2,
      title: t('purchasing.noSuppliers'),
      description: t('purchasing.emptyStates.suppliersDescription') || 'Add your first supplier to start creating purchase orders',
      action: canManageSuppliers ? {
        label: t('purchasing.addSupplier'),
        onClick: () => setShowSupplierModal(true),
      } : undefined,
    },
    pr: {
      icon: FileText,
      title: t('purchasing.noPRs'),
      description: t('purchasing.emptyStates.prsDescription') || 'Create a request when your branch needs supplies',
      action: {
        label: t('purchasing.createPR'),
        onClick: () => setShowPRModal(true),
      },
      tips: [
        t('purchasing.emptyStates.prTip1') || 'PRs are automatically routed to HQ for approval',
        t('purchasing.emptyStates.prTip2') || 'Mark urgent requests for faster processing',
      ],
    },
    po: {
      icon: ShoppingCart,
      title: t('purchasing.noPOs'),
      description: t('purchasing.emptyStates.posDescription') || 'Create purchase orders from approved PRs',
      action: canCreatePO ? {
        label: t('purchasing.createPO'),
        onClick: () => setShowPOModal(true),
      } : undefined,
    },
    grn: {
      icon: Package,
      title: t('purchasing.noGRNs'),
      description: t('purchasing.emptyStates.grnsDescription') || 'GRNs are created when goods arrive from purchase orders',
    },
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="border-b bg-card p-4">
        <h1 className="text-2xl font-bold">{t('purchasing.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('purchasing.subtitle')}</p>
      </div>

      {/* Tabs */}
      <div className="border-b bg-card px-4">
        <div className="flex gap-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setStatusFilter('');
                  setSearchQuery('');
                }}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary font-medium'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Dashboard Metrics */}
          {activeTab === 'suppliers' && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard
                title={t('purchasing.dashboard.totalSuppliers') || 'Total Suppliers'}
                value={supplierMetrics.total}
                icon={Building2}
                color="blue"
              />
              <MetricCard
                title={t('purchasing.dashboard.preferredSuppliers') || 'Preferred'}
                value={supplierMetrics.preferred}
                icon={Star}
                color="amber"
              />
              <MetricCard
                title={t('purchasing.dashboard.avgQuality') || 'Avg Quality'}
                value={`${supplierMetrics.avgQuality.toFixed(1)}/5`}
                icon={Award}
                color="green"
              />
              <MetricCard
                title={t('purchasing.dashboard.avgLeadTime') || 'Avg Lead Time'}
                value={`${supplierMetrics.avgLeadTime}d`}
                icon={Clock}
                color="purple"
              />
            </div>
          )}

          {activeTab === 'pr' && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard
                title={t('purchasing.dashboard.pendingApproval') || 'Pending Approval'}
                value={prMetrics.pending}
                icon={Clock}
                color="orange"
                onClick={() => setStatusFilter('pending')}
              />
              <MetricCard
                title={t('purchasing.dashboard.urgentRequests') || 'Urgent'}
                value={prMetrics.urgent}
                icon={AlertCircle}
                color="red"
              />
              <MetricCard
                title={t('purchasing.dashboard.thisWeek') || 'This Week'}
                value={prMetrics.thisWeek}
                icon={Calendar}
                color="blue"
              />
              <MetricCard
                title={t('purchasing.dashboard.totalEstimated') || 'Total Est'}
                value={formatCurrency(prMetrics.totalEstimated)}
                icon={TrendingUp}
                color="green"
              />
            </div>
          )}

          {activeTab === 'po' && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard
                title={t('purchasing.dashboard.inTransit') || 'In Transit'}
                value={poMetrics.inTransit}
                icon={ShoppingCart}
                color="blue"
                onClick={() => setStatusFilter('sent')}
              />
              <MetricCard
                title={t('purchasing.dashboard.overdue') || 'Overdue'}
                value={poMetrics.overdue}
                icon={AlertCircle}
                color="red"
              />
              <MetricCard
                title={t('purchasing.dashboard.thisMonth') || 'This Month'}
                value={poMetrics.thisMonth}
                icon={Calendar}
                color="purple"
              />
              <MetricCard
                title={t('common.total') || 'Total'}
                value={formatCurrency(poMetrics.total)}
                icon={TrendingUp}
                color="green"
              />
            </div>
          )}

          {activeTab === 'grn' && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard
                title={t('purchasing.dashboard.pendingReview') || 'Pending Review'}
                value={grnMetrics.pending}
                icon={Clock}
                color="orange"
                onClick={() => setStatusFilter('pending_review')}
              />
              <MetricCard
                title={t('purchasing.dashboard.withIssues') || 'With Issues'}
                value={grnMetrics.withIssues}
                icon={AlertTriangle}
                color="red"
                onClick={() => setStatusFilter('has_discrepancies')}
              />
              <MetricCard
                title={t('purchasing.dashboard.completed') || 'Completed'}
                value={grnMetrics.completed}
                icon={CheckCircle}
                color="green"
              />
              <MetricCard
                title={t('purchasing.dashboard.inQuarantine') || 'In Quarantine'}
                value={grnMetrics.quarantine}
                icon={AlertCircle}
                color="purple"
              />
            </div>
          )}

          {/* Toolbar */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4 flex-1">
              {/* Search */}
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('common.search')}
                  className="w-full pl-9 pr-3 py-2 rounded-lg border bg-background text-sm"
                />
              </div>

              {/* Status Filter */}
              {activeTab !== 'suppliers' && (
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="rounded-lg border bg-background px-3 py-2 text-sm"
                >
                  <option value="">{t('common.all')}</option>
                  {activeTab === 'pr' && (
                    <>
                      <option value="pending">{t('purchasing.pending')}</option>
                      <option value="approved">{t('purchasing.approved')}</option>
                      <option value="rejected">{t('purchasing.rejected')}</option>
                      <option value="converted">{t('purchasing.convertedToPO')}</option>
                    </>
                  )}
                  {activeTab === 'po' && (
                    <>
                      <option value="draft">{t('purchasing.draft')}</option>
                      <option value="sent">{t('purchasing.sent')}</option>
                      <option value="confirmed">{t('purchasing.confirmed')}</option>
                      <option value="partially_received">{t('purchasing.partiallyReceived')}</option>
                      <option value="completed">{t('purchasing.completed')}</option>
                      <option value="cancelled">{t('purchasing.cancelled')}</option>
                    </>
                  )}
                  {activeTab === 'grn' && (
                    <>
                      <option value="draft">{t('purchasing.draft')}</option>
                      <option value="pending_review">{t('purchasing.pendingReview')}</option>
                      <option value="has_discrepancies">{t('purchasing.hasDiscrepancies')}</option>
                      <option value="completed">{t('purchasing.completed')}</option>
                    </>
                  )}
                </select>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* View Toggle */}
              <div className="flex items-center rounded-lg border bg-background p-1">
                <button
                  onClick={() => setViewMode('cards')}
                  className={`p-1.5 rounded-md transition-colors ${
                    viewMode === 'cards' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  aria-label={t('purchasing.views.cards') || 'Cards view'}
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-1.5 rounded-md transition-colors ${
                    viewMode === 'table' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  aria-label={t('purchasing.views.table') || 'Table view'}
                >
                  <LayoutList className="h-4 w-4" />
                </button>
              </div>

              {/* Add Button */}
              {activeTab !== 'grn' && (
                <button
                  onClick={() => {
                    if (activeTab === 'suppliers') setShowSupplierModal(true);
                    else if (activeTab === 'pr') setShowPRModal(true);
                    else if (activeTab === 'po') setShowPOModal(true);
                  }}
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  {activeTab === 'suppliers' && t('purchasing.addSupplier')}
                  {activeTab === 'pr' && t('purchasing.createPR')}
                  {activeTab === 'po' && t('purchasing.createPO')}
                </button>
              )}
            </div>
          </div>

          {/* Cards Grid */}
          <div className={viewMode === 'cards' ? 'grid grid-cols-1 lg:grid-cols-2 gap-4' : 'space-y-3'}>
            {/* Suppliers */}
            {activeTab === 'suppliers' && (
              <>
                {suppliers && suppliers.length > 0 ? (
                  suppliers.map((supplier) => (
                    <SupplierCard
                      key={supplier._id}
                      supplier={supplier}
                      onView={() => setShowSupplierDetail(supplier)}
                      onEdit={() => {
                        setEditingSupplier(supplier);
                        setShowSupplierModal(true);
                      }}
                      onDelete={() => handleDeleteSupplier(supplier)}
                      canManage={canManageSuppliers}
                    />
                  ))
                ) : (
                  <div className="col-span-full">
                    <EmptyState {...emptyStates.suppliers} />
                  </div>
                )}
              </>
            )}

            {/* Purchase Requests */}
            {activeTab === 'pr' && (
              <>
                {purchaseRequests.length > 0 ? (
                  purchaseRequests.map((pr) => (
                    <PRCard
                      key={pr._id}
                      pr={pr}
                      onClick={() => setSelectedPR(pr)}
                      onApprove={() => handleApprovePR(pr)}
                      onReject={() => handleRejectPR(pr)}
                      onConvertToPO={() => handleConvertToPO(pr)}
                      canApprove={canApprovePR}
                      canCreatePO={canCreatePO}
                    />
                  ))
                ) : (
                  <div className="col-span-full">
                    <EmptyState {...emptyStates.pr} />
                  </div>
                )}
              </>
            )}

            {/* Purchase Orders */}
            {activeTab === 'po' && (
              <>
                {purchaseOrders.length > 0 ? (
                  purchaseOrders.map((po) => (
                    <POCard
                      key={po._id}
                      po={po}
                      onClick={() => setSelectedPO(po)}
                      onSend={() => handleSendPO(po)}
                      onRecordGRN={() => handleRecordGRN(po)}
                    />
                  ))
                ) : (
                  <div className="col-span-full">
                    <EmptyState {...emptyStates.po} />
                  </div>
                )}
              </>
            )}

            {/* Goods Received Notes */}
            {activeTab === 'grn' && (
              <>
                {grns.length > 0 ? (
                  grns.map((grn) => (
                    <GRNCard
                      key={grn._id}
                      grn={grn}
                      onClick={() => setSelectedGRN(grn)}
                    />
                  ))
                ) : (
                  <div className="col-span-full">
                    <EmptyState {...emptyStates.grn} />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showSupplierModal && (
        <SupplierModal
          supplier={editingSupplier}
          onClose={() => {
            setShowSupplierModal(false);
            setEditingSupplier(null);
          }}
          onSuccess={() => {
            setShowSupplierModal(false);
            setEditingSupplier(null);
            refetchSuppliers();
          }}
        />
      )}

      {showSupplierDetail && (
        <SupplierDetailModal
          supplier={showSupplierDetail}
          onClose={() => setShowSupplierDetail(null)}
          onEdit={() => {
            setEditingSupplier(showSupplierDetail);
            setShowSupplierDetail(null);
            setShowSupplierModal(true);
          }}
        />
      )}

      {showPRModal && (
        <CreatePRModal
          onClose={() => setShowPRModal(false)}
          onSuccess={() => {
            setShowPRModal(false);
            refetchPRs();
          }}
        />
      )}

      {selectedPR && (
        <PRDetailModal
          pr={selectedPR}
          onClose={() => setSelectedPR(null)}
          onConvertToPO={() => {
            handleConvertToPO(selectedPR);
            setSelectedPR(null);
          }}
          onApprove={() => {
            handleApprovePR(selectedPR);
            setSelectedPR(null);
          }}
          onReject={() => {
            handleRejectPR(selectedPR);
            setSelectedPR(null);
          }}
          onRefresh={refetchPRs}
        />
      )}

      {showPOModal && (
        <CreatePOModal
          sourcePR={prForPO}
          onClose={() => {
            setShowPOModal(false);
            setPrForPO(null);
          }}
          onSuccess={() => {
            setShowPOModal(false);
            setPrForPO(null);
            refetchPOs();
            refetchPRs();
          }}
        />
      )}

      {selectedPO && (
        <PODetailModal
          po={selectedPO}
          onClose={() => setSelectedPO(null)}
          onRecordGRN={() => {
            handleRecordGRN(selectedPO);
            setSelectedPO(null);
          }}
          onRefresh={refetchPOs}
        />
      )}

      {showGRNModal && (
        <CreateGRNModal
          sourcePO={poForGRN}
          onClose={() => {
            setShowGRNModal(false);
            setPoForGRN(null);
          }}
          onSuccess={() => {
            setShowGRNModal(false);
            setPoForGRN(null);
            refetchGRNs();
            refetchPOs();
          }}
        />
      )}

      {selectedGRN && (
        <GRNDetailModal
          grn={selectedGRN}
          onClose={() => setSelectedGRN(null)}
          onRefresh={refetchGRNs}
        />
      )}
    </div>
  );
}

export default PurchasingPage;
